import { Check, Copy, Search, Terminal, X } from "lucide-react";
import { useMemo, useState, type ReactNode } from "react";
import { copyText } from "./clipboard.ts";

export function dateTime(value?: string | null) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString("zh-CN", {
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  });
}

export function JsonSyntax({ json }: { json: string }) {
  if (!json) return <span className="json-null">null</span>;

  const tokens: ReactNode[] = [];
  const regex =
    /("(\\u[a-zA-Z0-9]{4}|\\[^u]|[^\\"])*"(\s*:)?|\b(true|false|null)\b|-?\d+(?:\.\d*)?(?:[eE][+\-]?\d+)?|[{}\[\],:])/g;

  let lastIndex = 0;
  let match: RegExpExecArray | null;

  try {
    while ((match = regex.exec(json)) !== null) {
      const text = match[0];
      const matchIndex = match.index;

      if (matchIndex > lastIndex) {
        tokens.push(json.substring(lastIndex, matchIndex));
      }

      if (text.endsWith(":")) {
        const keyText = text.slice(0, -1);
        tokens.push(
          <span key={matchIndex} className="json-key">
            {keyText}
          </span>,
        );
        tokens.push(
          <span key={`${matchIndex}-colon`} className="json-punct">
            :{" "}
          </span>,
        );
      } else if (text.startsWith('"')) {
        tokens.push(
          <span key={matchIndex} className="json-string">
            {text}
          </span>,
        );
      } else if (text === "true" || text === "false") {
        tokens.push(
          <span key={matchIndex} className="json-boolean">
            {text}
          </span>,
        );
      } else if (text === "null") {
        tokens.push(
          <span key={matchIndex} className="json-null">
            {text}
          </span>,
        );
      } else if (/^-?\d/.test(text)) {
        tokens.push(
          <span key={matchIndex} className="json-number">
            {text}
          </span>,
        );
      } else {
        tokens.push(
          <span key={matchIndex} className="json-punct">
            {text}
          </span>,
        );
      }

      lastIndex = regex.lastIndex;
    }

    if (lastIndex < json.length) {
      tokens.push(json.substring(lastIndex));
    }
  } catch {
    return <span>{json}</span>;
  }

  return <span className="json-syntax-stream">{tokens}</span>;
}

function getEventTypeClass(eventType: string): string {
  const upper = (eventType || "").toUpperCase();
  if (upper.includes("FAIL") || upper.includes("ERROR") || upper.includes("BLOCKED")) {
    return "evt-danger";
  }
  if (
    upper.includes("EXECUTION") ||
    upper.includes("AUTH") ||
    upper.includes("APPROVAL")
  ) {
    return "evt-purple";
  }
  if (
    upper.includes("COMPLETE") ||
    upper.includes("SUCCESS") ||
    upper.includes("FINISH")
  ) {
    return "evt-success";
  }
  if (upper.includes("QUEUED") || upper.includes("CLAIM")) {
    return "evt-cyan";
  }
  return "evt-blue";
}

export function EventStreamConsole({ events }: { events: any[] }) {
  const [filterQuery, setFilterQuery] = useState("");
  const [copiedId, setCopiedId] = useState<string | null>(null);

  const query = filterQuery.trim().toLowerCase();
  const filteredEvents = useMemo(() => {
    if (!query) return events;
    return (events || []).filter((evt) => {
      const type = (evt.event_type || "").toLowerCase();
      const payload = (evt.payload_json || "").toLowerCase();
      const time = dateTime(evt.created_at).toLowerCase();
      return type.includes(query) || payload.includes(query) || time.includes(query);
    });
  }, [events, query]);

  const handleCopy = async (payload: string, eventId: string) => {
    const ok = await copyText(payload);
    if (ok) {
      setCopiedId(eventId);
      setTimeout(() => {
        setCopiedId((curr) => (curr === eventId ? null : curr));
      }, 1500);
    }
  };

  return (
    <div className="event-stream-console">
      <div className="console-toolbar">
        <div className="toolbar-left">
          <Terminal size={15} className="terminal-icon" />
          <span className="console-heading">事件流日志 (EVENT STREAM)</span>
          <span className="console-badge">
            {query
              ? `${filteredEvents.length} / ${events.length} 条`
              : `共 ${events.length} 条记录`}
          </span>
        </div>

        <div className="toolbar-right">
          <div className="console-search-box">
            <Search size={13} className="search-icon" />
            <input
              type="text"
              placeholder="过滤事件名称或报文 Payload..."
              value={filterQuery}
              onChange={(e) => setFilterQuery(e.target.value)}
              aria-label="过滤事件日志"
            />
            {filterQuery && (
              <button
                type="button"
                className="search-clear-btn"
                onClick={() => setFilterQuery("")}
                title="清空过滤"
              >
                <X size={12} />
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="console-body">
        {filteredEvents.length > 0 ? (
          <div className="event-stream-rows">
            {filteredEvents.map((event: any) => {
              const isCopied = copiedId === event.event_id;
              const typeClass = getEventTypeClass(event.event_type);

              return (
                <div key={event.event_id} className="event-stream-row">
                  <time className="evt-time">{dateTime(event.created_at)}</time>
                  <strong className={`evt-name ${typeClass}`}>
                    {event.event_type}
                  </strong>
                  <div className="evt-payload">
                    <code>
                      <JsonSyntax json={event.payload_json} />
                    </code>
                  </div>
                  <div className="evt-actions">
                    <button
                      type="button"
                      className={`evt-copy-btn ${isCopied ? "copied" : ""}`}
                      onClick={() => handleCopy(event.payload_json, event.event_id)}
                      title="复制完整 JSON Payload"
                    >
                      {isCopied ? (
                        <>
                          <Check size={12} />
                          <span>已复制</span>
                        </>
                      ) : (
                        <>
                          <Copy size={12} />
                          <span>复制</span>
                        </>
                      )}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="console-empty-state">
            <Search size={22} />
            <p>未找到匹配“{filterQuery}”的事件记录</p>
            <button
              type="button"
              className="clear-filter-link"
              onClick={() => setFilterQuery("")}
            >
              清除过滤条件
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
